import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDatepicker } from '@angular/material/datepicker';
import { DatePipe } from '@angular/common';
import { NotificationService } from '../../core/core.module';
import { LgvUserStateLicensingService, UserStateLicense } from '../../core/services/lgv-user-licensing.service';
import { CompanyStateLicense, LgvCompanyStateLicensingService, StateLicensingService } from '../../core/services/lgv-company-licensing.service';
import { LgvStatesHelper } from '../../core/utility/lgv-states-helper';

export enum LicenseStatus {
  Approved = 'Approved',
  PendingReview = 'Pending - Review',
  ApprovedInactive = 'Approved - Inactive',
  TerminatedFailedToRenew = 'Terminated - Failed to Renew',
  PendingIncomplete = 'Pending - Incomplete'
}

export interface LicenseEditModalData {
  entityId: string;
  license?: UserStateLicense | CompanyStateLicense;
  entityType: 'user' | 'company';
}

@Component({
  selector: 'lgv-license-edit-modal',
  templateUrl: './license-edit-modal.component.html',
  styleUrls: ['./license-edit-modal.component.scss']
})
export class LicenseEditModalComponent implements OnInit, AfterViewInit {
  @ViewChild('picker') picker: MatDatepicker<Date>;
  licenseForm: UntypedFormGroup;
  isEditMode: boolean;
  entityId: string;
  entityType: 'user' | 'company';
  license: UserStateLicense | CompanyStateLicense;
  statuses = Object.values(LicenseStatus);
  expirationYear: string;
  isSubmitting = false;
  stateControl = new FormControl('', [Validators.required]);
  allStates = LgvStatesHelper.getAllStates();

  private get licensingService(): StateLicensingService<UserStateLicense | CompanyStateLicense> {
    return this.entityType === 'user' ? this.userLicensingService : this.companyLicensingService;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private notificationSvc: NotificationService,
    private userLicensingService: LgvUserStateLicensingService,
    private companyLicensingService: LgvCompanyStateLicensingService,
    private lgvStatesHelper: LgvStatesHelper,
    public dialogRef: MatDialogRef<LicenseEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LicenseEditModalData,
    private datePipe: DatePipe
  ) {
    this.entityId = data.entityId;
    this.entityType = data.entityType;
    this.license = data.license;
    this.isEditMode = !!this.license;
    if (this.isEditMode && this.license && this.license.state) {
      let stateValue = this.license.state;
      
      if (stateValue.length > 2) {
        stateValue = LgvStatesHelper.getStateAbbreviation(stateValue);
      }
      
      this.stateControl.setValue(stateValue, {emitEvent: false});
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngAfterViewInit(): void {
    // Set the state value again after view init to ensure it's properly displayed
    if (this.isEditMode && this.license && this.license.state) {
      setTimeout(() => {
        // Handle both state name and state code formats
        let stateValue = this.license.state;
        
        // If the state is a full name (like "Florida"), convert to code (like "FL")
        if (stateValue.length > 2) {
          stateValue = LgvStatesHelper.getStateAbbreviation(stateValue);
        }
        
        console.log('AfterViewInit - Setting state control to:', stateValue);
        // Use emitEvent: false to prevent triggering valueChanges
        this.stateControl.setValue(stateValue, {emitEvent: false});
        console.log('AfterViewInit - State control value after set:', this.stateControl.value);
      }, 100); // Increase timeout to ensure component is fully initialized
    }
  }

  initializeForm(): void {
    this.licenseForm = this.fb.group({
      stateLicenseNumber: [''],
      expirationYear: [null, [Validators.required]],
      status: ['', [Validators.required]]
    });

    if (this.isEditMode && this.license) {
      console.log('Edit mode - Initial license year:', this.license.expirationYear);
      const date = new Date(parseInt(this.license.expirationYear), 0, 1);
      console.log('Converted to date:', date);
      let stateValue = this.license.state;
      
      if (stateValue.length > 2) {
        stateValue = LgvStatesHelper.getStateAbbreviation(stateValue);
      }
      
      this.licenseForm.patchValue({
        stateLicenseNumber: this.license.stateLicenseNumber,
        expirationYear: date,
        status: this.license.status || LicenseStatus.Approved
      });
      
      // Set the state control value
      const stateAbbreviation = LgvStatesHelper.getStateAbbreviation(this.license.state);
      console.log('Setting state control to:', stateAbbreviation, 'from license state:', this.license.state);
      this.stateControl.setValue(stateAbbreviation);
      
      console.log('Form after patch:', this.licenseForm.value);
      console.log('State control value:', this.stateControl.value);
    } else {
      this.licenseForm.patchValue({
        status: LicenseStatus.PendingReview
      });
    }

    // Subscribe to year changes
    this.licenseForm.get('expirationYear').valueChanges.subscribe(val => {
      console.log('Year value changed to:', val);
    });

    // Subscribe to state changes
    this.stateControl.valueChanges.subscribe(val => {
      console.log('State value changed to:', val);
    });
  }

  onCancel(): void {
    this.dialogRef.close({ action: false });
  }

  onSave(): void {
    if (this.licenseForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const yearValue = this.licenseForm.get('expirationYear').value;
    const stateValue = this.stateControl.value;
    
    let finalStateValue = stateValue;
    
    if (stateValue && stateValue.length === 2) {
      const stateName = LgvStatesHelper.getStateName(stateValue);
      if (stateName) {
        finalStateValue = stateName;
      }
    }
    
    const formValue = {
      ...this.licenseForm.getRawValue(),
      expirationYear: yearValue instanceof Date ? yearValue.getFullYear().toString() : yearValue.toString(),
      state: finalStateValue
    };

    const entityType = this.entityType === 'user' ? 'User' : 'Company';

    if (this.isEditMode) {
      this.licensingService.updateStateLicense(this.entityId, this.license.id, formValue)
        .subscribe({
          next: () => {
            this.notificationSvc.success(`${entityType} license has been updated`);
            this.dialogRef.close({ action: true });
          },
          error: () => {
            this.notificationSvc.error(`There was an error updating the ${entityType.toLowerCase()} license`);
            this.isSubmitting = false;
          }
        });
    } else {
      this.licensingService.addStateLicense(this.entityId, formValue)
        .subscribe({
          next: () => {
            this.notificationSvc.success(`${entityType} license has been added`);
            this.dialogRef.close({ action: true });
          },
          error: () => {
            this.notificationSvc.error(`There was an error adding the ${entityType.toLowerCase()} license`);
            this.isSubmitting = false;
          }
        });
    }
  }

  chosenYearHandler(normalizedYear: Date) {
    this.licenseForm.patchValue({
      expirationYear: normalizedYear
    });
    this.picker.close();
  }
} 